import { HlsVideoPlayer } from './hls-video-player'
import style from './workout-video.module.scss'
import { SessionContext } from '../../context/session-context'
import { Workout } from '../../dao/core-dao'
import { useCompleteProgrammeWorkout } from '../../hook/use-complete-programme-workout'
import { useCompleteWorkout } from '../../hook/use-complete-workout'
import { useCompletedProgrammeWorkouts, useProgrammeWorkouts } from '../../hook/use-programme-workouts'
import { storePendingProgrammeOverviewHighlight } from '../../util/programme-overview-pending-highlight'
import { fileService } from '../../service/file-service'
import { EventBusActionType, EventBusEntityType, eventBusUtil } from '../../util/event-bus-util'
import { logger } from '../../util/logger'
import { ReactionButtons } from '../community/reaction-buttons'
import { featuredUpsellService } from '../../service/featured-upsell-service'
import { MixpanelEvent, mixpanelService } from '../../service/mixpanel-service'
import React, {
  ForwardedRef,
  RefObject,
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Button, Col, Image, Modal, Row } from 'react-bootstrap'
import { useCatalogAccess } from '../../hook/use-catalog-access'
import { useFeaturedWorkouts } from '../../hook/use-featured-workouts'

export const WorkoutVideo = ({
  workout,
  programmeId,
  programmeWorkoutId,
  onVideoCompleted,
}: {
  workout: Workout
  programmeId?: string
  programmeWorkoutId?: string
  onVideoCompleted?: () => void
}): React.ReactElement => {
  const VIDEO_COMPLETED_PERCENT = 0.75
  // GH-866: the server predicate, not `SessionContext.hasActiveSubscription`. Only the three
  // call sites in this feature move; `useMembership` and the profile screens stay as they are.
  // The fail-open flag on purpose: this gates an ATTEMPT, where a false negative paywalls a
  // paying subscriber. Hiding a surface would want `isDefinitelyEntitled` instead.
  const { hasFullCatalogAccess } = useCatalogAccess()
  const { toggleSubscriptionModal, completeWorkout, me, completedWorkoutIds, hustlerConfig } = useContext(SessionContext)
  const { featuredWorkouts } = useFeaturedWorkouts()

  const isFreeWorkout = useMemo(() => featuredWorkouts.some(({ id }) => id === workout.id), [featuredWorkouts, workout.id])

  /**
   * Assembled at the trigger rather than fetched: which free workouts this account has played
   * is `completedWorkoutIds` intersected with the featured set, and time since sign-up comes
   * off the hustler record. No endpoint backs any of it.
   */
  const paywallContextBase = useMemo(
    () => ({
      blockedWorkoutId: workout.id,
      playedFeaturedWorkoutIds: featuredWorkouts.filter(({ id }) => completedWorkoutIds.has(id)).map(({ id }) => id),
      daysSinceSignUp: me?.createdAt
        ? Math.floor((Date.now() - new Date(me.createdAt).getTime()) / (24 * 60 * 60 * 1000))
        : undefined,
    }),
    [workout.id, featuredWorkouts, completedWorkoutIds, me?.createdAt],
  )
  const completeWorkoutMutation = useCompleteWorkout()
  const completeProgrammeWorkout = useCompleteProgrammeWorkout()
  const programmeIdForQueries = programmeId ?? ''
  const { data: programmeWorkouts = [] } = useProgrammeWorkouts(programmeIdForQueries)
  const { data: completedProgrammeWorkouts = [] } = useCompletedProgrammeWorkouts(programmeIdForQueries)

  const playerRef = useRef<HTMLVideoElement>(null)
  const isCompletedRef = useRef(false)
  const isCompletedWorkoutRequestSentRef = useRef(false)
  const isVideoPlayingRef = useRef(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const handleVideoStart = useCallback((): void => {
    if (!hasFullCatalogAccess && !isFreeWorkout && playerRef.current) {
      playerRef.current.pause()
      toggleSubscriptionModal({ ...paywallContextBase, source: 'blocked-play' })
    }
    logger.debug('handleVideoStart')
    if (!isVideoPlayingRef.current) {
      eventBusUtil().emit({
        action: EventBusActionType.CREATE,
        entity: EventBusEntityType.VIDEO_PLAYBACK,
        meta: { workout },
      })
    }
    isVideoPlayingRef.current = true
  }, [hasFullCatalogAccess, isFreeWorkout, paywallContextBase, playerRef, toggleSubscriptionModal, workout])

  const handleVideoEnd = (): void => {
    logger.debug('handleVideoEnd')
    setShowConfirmationModal(isCompletedRef.current)
    isVideoPlayingRef.current = false
  }

  /**
   * The post-workout upgrade prompt. There is no button and no choice: once "Great Work!" is
   * dismissed the paywall comes up on its own, for any account that cannot play the whole
   * catalog and has just finished a *free* workout — the moment they have actually experienced
   * the product. `showConfirmationModal` is only ever set from the completion path, so an
   * abandoned workout never reaches this.
   *
   * ⚠️ Fires from `onExited`, not from the Finish handler. The completion modal is
   * react-bootstrap and the paywall is a Radix dialog, and both manage `body` scroll: opening
   * them in one tick lets react-bootstrap's exit cleanup restore `body` overflow *after* Radix
   * has locked it, leaving the page scrollable behind the paywall and a stale backdrop in the
   * tree. Waiting for the exit transition also *is* the intended behaviour — the paywall
   * follows the modal, it does not stack on it.
   *
   * ⚠️ This depends on the modal keeping `animation` at its default `true`. react-bootstrap
   * passes `transition: animation ? DialogTransition : undefined`, so with `animation={false}`
   * there is no transition to exit and `onExited` is never called — the paywall would simply
   * stop appearing, with no error anywhere. Don't disable the fade on this modal.
   *
   * `featuredUpsell.mode` still gates this. `OFF` remains a server-side kill switch, which
   * matters more now that the prompt cannot be skipped.
   */
  const handleConfirmationExited = useCallback((): void => {
    const shouldOpen = featuredUpsellService.shouldOpenAfterCompletion({
      hasFullCatalogAccess,
      isFreeWorkout,
      config: hustlerConfig.featuredUpsell,
    })
    if (!shouldOpen) return

    featuredUpsellService.recordShown()
    // Impressions stay separate from the blocked-play trigger so the two routes into the
    // subscribe flow can be compared rather than conflated. There is no companion "clicked"
    // event any more: with the paywall auto-opening, the impression *is* the open, and
    // conversion is measured from the paywall itself via `source`.
    mixpanelService.trackFeatured(MixpanelEvent.FEATURED_UPSELL_SHOWN, {
      workoutTitle: workout.title,
      blockedWorkoutId: paywallContextBase.blockedWorkoutId,
      playedFeaturedCount: paywallContextBase.playedFeaturedWorkoutIds.length,
      daysSinceSignUp: paywallContextBase.daysSinceSignUp,
    })
    // `toggleSubscriptionModal` toggles rather than opens, so this must run exactly once per
    // close — a transition callback, never an effect, which StrictMode would double-invoke
    // straight back into a closed paywall.
    toggleSubscriptionModal({ ...paywallContextBase, source: 'featured-upsell' })
  }, [
    hasFullCatalogAccess,
    isFreeWorkout,
    hustlerConfig.featuredUpsell,
    workout.title,
    paywallContextBase,
    toggleSubscriptionModal,
  ])
  const workoutCompletedRequest = useCallback((): Promise<unknown> => {
    if (programmeId && programmeWorkoutId) {
      return completeProgrammeWorkout.mutateAsync({
        programmeId,
        programmeWorkoutId,
        maxHeartRate: 0,
        calories: 0,
        averageHeartRate: 0,
        hustleScore: 0,
      })
    } else {
      return completeWorkoutMutation.mutateAsync({ workoutId: workout.id })
    }
  }, [completeWorkoutMutation, completeProgrammeWorkout, programmeId, programmeWorkoutId, workout.id])
  const handleProgress = useCallback(
    (_event: any): void => {
      if (!playerRef.current) {
        return
      }

      if (playerRef.current.duration * VIDEO_COMPLETED_PERCENT < playerRef.current.currentTime) {
        if (!isCompletedRef.current) {
          // To notify outside of this component that workout is completed via eventBus
          isCompletedRef.current = true
          eventBusUtil().emit({
            action: EventBusActionType.REMOVE,
            entity: EventBusEntityType.VIDEO_PLAYBACK,
            meta: { workout },
          })

          if (onVideoCompleted) onVideoCompleted()
        }

        if (!isCompletedWorkoutRequestSentRef.current) {
          // To notify our server that workout is completed
          // We had a problem that this request failed, and we were sending to mixpanel multiple times
          // See More https://hurricanestudio.atlassian.net/browse/GRND-1417
          isCompletedWorkoutRequestSentRef.current = true
          workoutCompletedRequest()
            .then(() => {
              logger.debug('completeWorkout')
              completeWorkout(workout)
              if (programmeId && programmeWorkoutId) {
                storePendingProgrammeOverviewHighlight(
                  programmeId,
                  programmeWorkouts,
                  completedProgrammeWorkouts,
                  programmeWorkoutId,
                )
              }
            })
            .catch((_error) => {
              isCompletedWorkoutRequestSentRef.current = false
            })
        }
      }
    },
    [
      completeWorkout,
      completedProgrammeWorkouts,
      onVideoCompleted,
      programmeId,
      programmeWorkoutId,
      programmeWorkouts,
      workout,
      workoutCompletedRequest,
    ],
  )

  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.addEventListener('play', handleVideoStart)
      playerRef.current.addEventListener('ended', handleVideoEnd)
      playerRef.current.addEventListener('timeupdate', handleProgress)
    }
  }, [handleProgress, handleVideoStart])
  const canPlay = hasFullCatalogAccess || isFreeWorkout

  /**
   * ⚠️ This early return is gated on `canPlay` on purpose — do not restore the bare
   * `if (!workout.videoUrl)` it replaced.
   *
   * Since GH-866 the server withholds `videoUrl` from accounts that may not play the workout,
   * which is exactly the case the paywall branch below exists to serve. A `videoUrl`-only
   * check therefore returned `<></>` *before* reaching that branch, and the paywall poster,
   * the play-to-subscribe link and the reaction buttons all silently vanished for every
   * non-subscriber. What remains a genuine nothing-to-render case is an entitled viewer whose
   * workout has no URL at all — a data problem, not a paywall.
   */
  if (canPlay && !workout.videoUrl) {
    return <></>
  }
  return (
    <Row className="align-items-center text-center">
      <Col>
        <div className={style.videoContainer}>
          {canPlay && workout.videoUrl ? (
            <VideoPlayer videoUrl={workout.videoUrl} imagePublicFileId={workout.imagePublicFileId} ref={playerRef} />
          ) : (
            <a
              href="/#"
              onClick={(e) => {
                e.preventDefault()
                toggleSubscriptionModal({ ...paywallContextBase, source: 'blocked-play' })
              }}
            >
              <Image src={fileService.publicUrlFromFileId(workout.imagePublicFileId)} fluid />
            </a>
          )}
          <div className={style.reactionOverlay}>
            <ReactionButtons entityId={workout.id} entityType="workout" />
          </div>
        </div>
        <div className={style.workoutTitle}>{workout.title}</div>
        {/*
          Unchanged from before featured workouts: its own copy, its own Finish button, nothing
          about membership. The upgrade prompt is not a control in here — it follows this modal
          out, from `onExited`.
        */}
        <Modal
          show={showConfirmationModal}
          centered={true}
          contentClassName={style.modal}
          className="align-items-center text-center"
          onExited={handleConfirmationExited}
        >
          <div className={style.modalTitle}>Great Work!</div>
          <div className={style.modalText}>
            Well done with your workout! To see your stats and create a community post please download our app.
          </div>
          <div>
            <Button onClick={() => setShowConfirmationModal(false)}>Finish</Button>
          </div>
        </Modal>
      </Col>
    </Row>
  )
}
type VideoPlayerProps = {
  videoUrl: string
  imagePublicFileId: string
}
const VideoPlayer = memo(
  forwardRef((props: VideoPlayerProps, ref: ForwardedRef<HTMLVideoElement>) => {
    const DEFAULT_QUALITY_LEVEL = 2
    return (
      <HlsVideoPlayer
        src={props.videoUrl}
        autoPlay={false}
        controls={true}
        _playerRef={ref as RefObject<HTMLVideoElement>}
        width="100%"
        height="auto"
        poster={fileService.publicUrlFromFileId(props.imagePublicFileId)}
        hlsConfig={{
          startLevel: DEFAULT_QUALITY_LEVEL,
        }}
        className={style.videoPlayer}
      />
    )
  }),
)
